import { qs, request } from '@/shared/lib/http'

import type {
  CommonCode,
  CreateOrgBody,
  CreateProblemCardBody,
  CreateSolutionCardBody,
  CreatedOrg,
  CreatedProblemCard,
  CreatedSolutionCard,
  MapLayers,
  MapMatchedProblem,
  MapUnmetProblem,
  OrgDetail,
  OrgSummary,
  Paged,
  PostDetail,
  PostSummary,
  ProblemCard,
  ProblemCardEditForm,
  ProblemMatch,
  Region,
  SolutionCard,
  UpdateProblemCardBody,
  UpdatedProblemCard,
  UploadedOrgLogo,
} from '@/shared/lib/apiTypes'

export const api = {
  commonCodes: (group?: string) =>
    request<{ items: CommonCode[] }>(`/api/common-codes${qs({ group })}`),

  regions: () => request<{ items: Region[] }>('/api/regions'),

  mapLayers: () => request<MapLayers>('/api/map/layers'),

  orgs: () => request<{ items: OrgSummary[] }>('/api/orgs'),

  org: (id: string) => request<OrgDetail>(`/api/orgs/${id}`),

  solutionCards: (params: {
    category?: string
    target?: string
    helpType?: string
    serviceArea?: string
    q?: string
    limit?: number
    offset?: number
  }) => request<Paged<SolutionCard>>(`/api/solution-cards${qs(params)}`),

  solutionCard: (id: string) => request<SolutionCard>(`/api/solution-cards/${id}`),

  problemCards: (params: {
    category?: string
    target?: string
    region?: string
    urgency?: string
    limit?: number
    offset?: number
  }) => request<Paged<ProblemCard>>(`/api/problem-cards${qs(params)}`),

  problemCard: (id: string) => request<ProblemCard>(`/api/problem-cards/${id}`),

  problemMatches: (id: string) =>
    request<{ items: ProblemMatch[] }>(`/api/problem-cards/${id}/matches`),

  mapMatchedProblems: (params: { region?: string; limit?: number; offset?: number } = {}) =>
    request<Paged<MapMatchedProblem>>(`/api/map/matched-problems${qs(params)}`),

  mapUnmetProblems: (params: { region?: string; limit?: number; offset?: number } = {}) =>
    request<Paged<MapUnmetProblem>>(`/api/map/unmet-problems${qs(params)}`),

  posts: (params: { board?: string; q?: string; limit?: number; offset?: number }) =>
    request<Paged<PostSummary>>(`/api/posts${qs(params)}`),

  post: (id: string) => request<PostDetail>(`/api/posts/${id}`),

  orgNameAvailable: (name: string) =>
    request<{ available: boolean }>(`/api/orgs/name-available${qs({ name })}`),

  uploadOrgLogo: (file: File) => {
    const form = new FormData()
    form.append('file', file)
    return request<UploadedOrgLogo>('/api/org-logos', { method: 'POST', body: form })
  },

  createOrg: (body: CreateOrgBody) =>
    request<CreatedOrg>('/api/orgs', { method: 'POST', body: JSON.stringify(body) }),

  createSolutionCard: (body: CreateSolutionCardBody) =>
    request<CreatedSolutionCard>('/api/solution-cards', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  createProblemCard: (body: CreateProblemCardBody) =>
    request<CreatedProblemCard>('/api/problem-cards', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  problemCardEditForm: (id: string, token: string) =>
    request<ProblemCardEditForm>(`/api/problem-cards/${id}/edit${qs({ t: token })}`),

  updateProblemCard: (id: string, token: string, body: UpdateProblemCardBody) =>
    request<UpdatedProblemCard>(`/api/problem-cards/${id}/edit${qs({ t: token })}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
}
